import { FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import Button from "../components/Button";

type Status = "idle" | "sending" | "success" | "error";

export default function Contact() {
  const { t } = useTranslation();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [status, setStatus] = useState<Status>("idle");

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setStatus("sending");

    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          firstName,
          lastName,
          email,
          message,
        }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setStatus("success");
        setFirstName("");
        setLastName("");
        setEmail("");
        setMessage("");
      } else {
        setStatus("error");
      }
    } catch {
      setStatus("error");
    }
  }

  return (
    <div className="bg-kag-bg text-white">
      {/* HERO */}
      <section className="bg-kag-bg">
        <div className="mx-auto w-full max-w-page px-5 sm:px-8 md:px-10 lg:px-[60px] pt-12 sm:pt-16 lg:pt-[85px] pb-10 sm:pb-12 lg:pb-[60px] text-center">
          <p className="font-clash text-kag-eyebrow text-[13px] sm:text-[15px] lg:text-[17px] leading-[1.2] uppercase tracking-[0.18em]">
            {t("contact.eyebrow")}
          </p>
          <h1 className="mt-5 lg:mt-7 font-clash font-medium text-white uppercase text-[56px] sm:text-[80px] md:text-[104px] lg:text-[128px] xl:text-[140px] leading-[0.98] tracking-tight">
            {t("contact.title")}
          </h1>
        </div>
      </section>

      {/* FORM CARD */}
      <section className="bg-kag-bg pb-16 lg:pb-[80px]">
        <div className="mx-auto w-full max-w-page px-5 sm:px-6 md:px-8 lg:px-8">
          <div
            className="border-y border-l border-white/95 border-solid rounded-[28px] sm:rounded-[44px] lg:rounded-[60px] px-6 sm:px-10 lg:px-[80px] py-10 sm:py-12 lg:py-[64px]"
            style={{ borderRightWidth: 0, borderWidth: 1.006 }}
          >
            <div className="grid gap-10 lg:grid-cols-12 lg:gap-16 items-start">
              <div className="lg:col-span-5">
                <div className="flex items-start gap-3 sm:gap-4 lg:gap-5">
                  <span className="block bg-kag-red rounded-full mt-2 sm:mt-3 lg:mt-4 shrink-0 w-[12px] h-[12px] sm:w-[14px] sm:h-[14px] lg:w-[18px] lg:h-[18px]" />
                  <h2 className="font-clash font-semibold text-white text-[28px] sm:text-[36px] md:text-[42px] lg:text-[48px] leading-[1] tracking-tight">
                    {t("contact.cardTitle")}
                  </h2>
                </div>

                <div className="mt-8 lg:mt-10 flex flex-col gap-6 lg:gap-8">
                  <ContactRow
                    label={t("contact.entityLabel")}
                    value="KAG Defense LLC"
                  />
                </div>
              </div>

              <form onSubmit={handleSubmit} className="lg:col-span-7 flex flex-col gap-5 lg:gap-6">
                <div className="grid gap-5 lg:gap-6 sm:grid-cols-2">
                  <Field
                    label={t("contact.form.firstName")}
                    value={firstName}
                    onChange={setFirstName}
                    required
                    autoComplete="given-name"
                  />
                  <Field
                    label={t("contact.form.lastName")}
                    value={lastName}
                    onChange={setLastName}
                    required
                    autoComplete="family-name"
                  />
                </div>
                <Field
                  label={t("contact.form.email")}
                  type="email"
                  value={email}
                  onChange={setEmail}
                  required
                  autoComplete="email"
                />
                <FieldTextarea
                  label={t("contact.form.message")}
                  value={message}
                  onChange={setMessage}
                  required
                />
                <div className="pt-2 flex flex-wrap items-center gap-4">
                  <Button type="submit" variant="filled" disabled={status === "sending"}>
                    {status === "sending" ? t("contact.form.sending") : t("contact.form.submit")}
                  </Button>
                  {status === "success" && (
                    <p className="font-clash text-[14px] sm:text-[15px] text-[#6da1c7]" role="status">
                      {t("contact.form.success")}
                    </p>
                  )}
                  {status === "error" && (
                    <p className="font-clash text-[14px] sm:text-[15px] text-kag-red" role="alert">
                      {t("contact.form.error")}
                    </p>
                  )}
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function ContactRow({ label, value, href }: { label: string; value: string; href?: string }) {
  return (
    <div>
      <p className="font-clash font-medium text-[#6da1c7] text-[12px] sm:text-[13px] uppercase tracking-[0.18em]">
        {label}
      </p>
      {href ? (
        <a
          href={href}
          className="mt-2 inline-block font-clash font-semibold text-white text-[20px] sm:text-[22px] lg:text-[26px] leading-[1.2] tracking-tight hover:text-kag-red transition-colors break-all"
        >
          {value}
        </a>
      ) : (
        <p className="mt-2 font-clash font-semibold text-white text-[20px] sm:text-[22px] lg:text-[26px] leading-[1.2] tracking-tight">
          {value}
        </p>
      )}
    </div>
  );
}

type FieldProps = {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  required?: boolean;
  autoComplete?: string;
};

function Field({ label, value, onChange, type = "text", required, autoComplete }: FieldProps) {
  return (
    <label className="block">
      <span className="font-clash font-medium text-[#6da1c7] text-[12px] sm:text-[13px] uppercase tracking-[0.18em]">
        {label}
        {required && <span className="text-kag-red ml-1">*</span>}
      </span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        autoComplete={autoComplete}
        className="mt-2 block w-full bg-transparent border-0 border-b border-white/30 focus:border-white outline-none font-clash text-white text-[16px] sm:text-[18px] py-2 transition-colors"
      />
    </label>
  );
}

function FieldTextarea({ label, value, onChange, required }: Omit<FieldProps, "type" | "autoComplete">) {
  return (
    <label className="block">
      <span className="font-clash font-medium text-[#6da1c7] text-[12px] sm:text-[13px] uppercase tracking-[0.18em]">
        {label}
        {required && <span className="text-kag-red ml-1">*</span>}
      </span>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        rows={5}
        className="mt-2 block w-full bg-transparent border-0 border-b border-white/30 focus:border-white outline-none font-clash text-white text-[16px] sm:text-[18px] py-2 resize-none transition-colors"
      />
    </label>
  );
}
